import type { FeatureCollection, Polygon, Point } from 'geojson';
import { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { mapStyleService } from '../../map-engine/MapStyleService';
import type { Project } from '../../projects/types';
import { useProjectsGeoData } from '../hooks/useProjectsGeoData';
import 'maplibre-gl/dist/maplibre-gl.css';

/* ─── Constants ──────────────────────────────────────── */

const CENTER: [number, number] = [-60, -20]; // Sudamérica
const ZOOM = 3;
const PITCH = 18;

const CYAN = '#2FA4B5';
const CYAN_SELECTED = '#3DBFD3';
const CYAN_FILL = 'rgba(47, 164, 181, 0.10)';
const CYAN_LINE = 'rgba(47, 164, 181, 0.55)';

const SRC_BOUNDS = 'sa-admin-bounds';
const SRC_CENTROIDS = 'sa-admin-centroids';

/* ─── Layer helpers ──────────────────────────────────── */

function syncProjectLayers(
  map: MapLibreMap,
  bounds: FeatureCollection<Polygon>,
  centroids: FeatureCollection<Point>
): void {
  const existingBounds = map.getSource(SRC_BOUNDS);
  if (existingBounds) {
    (existingBounds as GeoJSONSource).setData(bounds);
    (map.getSource(SRC_CENTROIDS) as GeoJSONSource).setData(centroids);
    return;
  }

  // promoteId required for feature state (hover / selected)
  map.addSource(SRC_BOUNDS, { type: 'geojson', data: bounds, promoteId: 'id' });
  map.addSource(SRC_CENTROIDS, { type: 'geojson', data: centroids, promoteId: 'id' });

  const isHover = ['boolean', ['feature-state', 'hover'], false];
  const isSelected = ['boolean', ['feature-state', 'selected'], false];

  map.addLayer({
    id: 'sa-project-fill',
    type: 'fill',
    source: SRC_BOUNDS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paint: { 'fill-color': CYAN_FILL, 'fill-opacity': ['case', isHover, 0.22, 0.1] as any },
  });

  map.addLayer({
    id: 'sa-project-outline',
    type: 'line',
    source: SRC_BOUNDS,
    paint: { 'line-color': CYAN_LINE, 'line-width': 1.5, 'line-opacity': 1 },
  });

  map.addLayer({
    id: 'sa-project-point',
    type: 'circle',
    source: SRC_CENTROIDS,
    paint: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'circle-radius': ['case', isSelected, 9, isHover, 7, 5] as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'circle-color': ['case', isSelected, CYAN_SELECTED, CYAN] as any,
      'circle-opacity': 0.9,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'circle-stroke-width': ['case', isSelected, 2.5, 1.5] as any,
      'circle-stroke-color': '#ffffff',
    },
  });
}

function clearHover(map: MapLibreMap, id: string): void {
  map.setFeatureState({ source: SRC_BOUNDS, id }, { hover: false });
  map.setFeatureState({ source: SRC_CENTROIDS, id }, { hover: false });
}

function attachMapEvents(
  map: MapLibreMap,
  onSelect: (id: string) => void,
  hoveredRef: React.MutableRefObject<string | null>
): void {
  map.on('mouseenter', 'sa-project-point', e => {
    map.getCanvas().style.cursor = 'pointer';
    const id = e.features?.[0]?.properties?.id as string | undefined;
    if (!id || id === hoveredRef.current) return;
    if (hoveredRef.current) clearHover(map, hoveredRef.current);
    hoveredRef.current = id;
    map.setFeatureState({ source: SRC_BOUNDS, id }, { hover: true });
    map.setFeatureState({ source: SRC_CENTROIDS, id }, { hover: true });
  });

  map.on('mouseleave', 'sa-project-point', () => {
    map.getCanvas().style.cursor = '';
    if (hoveredRef.current) {
      clearHover(map, hoveredRef.current);
      hoveredRef.current = null;
    }
  });

  map.on('click', 'sa-project-point', e => {
    const id = e.features?.[0]?.properties?.id as string | undefined;
    if (id) onSelect(id);
  });

  map.on('click', 'sa-project-fill', e => {
    const id = e.features?.[0]?.properties?.id as string | undefined;
    if (id) onSelect(id);
  });
}

/* ─── Overlay subcomponent ───────────────────────────── */

function MapOverlay({
  count,
  loading,
  hasSelection,
}: {
  count: number;
  loading: boolean;
  hasSelection: boolean;
}): JSX.Element {
  return (
    <div className="sa-map-overlay">
      <span className="sa-map-overlay-title">Vista Territorial Global</span>
      <span className="sa-map-overlay-subtitle">
        {loading
          ? 'Cargando proyectos…'
          : `Sudamérica · ${count} proyecto${count !== 1 ? 's' : ''}`}
      </span>
      {!loading && count === 0 && (
        <span className="sa-map-overlay-hint">Sin proyectos con cobertura territorial</span>
      )}
      {!loading && count > 0 && !hasSelection && (
        <span className="sa-map-overlay-select">Hacé click en un proyecto</span>
      )}
    </div>
  );
}

/* ─── Main component ─────────────────────────────────── */

interface SuperadminMapSceneProps {
  projects: Project[];
  loading?: boolean;
  selectedProjectId: string | null;
  onProjectSelect: (id: string) => void;
}

/**
 * SuperadminMapScene — Vista territorial global MapLibre para la Consola Superadmin.
 *
 * Bloque 4. Añade hover (feature state), click → selección y sync de estado
 * seleccionado hacia el exterior vía `onProjectSelect`.
 *
 * @remarks
 * - Usa `promoteId: 'id'` en fuentes GeoJSON para habilitar feature state.
 * - Ref pattern para el callback onProjectSelect (evita stale closure).
 * - No renderiza la ficha de proyecto: responsabilidad del padre.
 */
export function SuperadminMapScene({
  projects,
  loading = false,
  selectedProjectId,
  onProjectSelect,
}: SuperadminMapSceneProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const hoveredIdRef = useRef<string | null>(null);
  const prevSelectedIdRef = useRef<string | null>(null);
  const onSelectRef = useRef(onProjectSelect);
  const [mapLoaded, setMapLoaded] = useState(false);

  const { boundsCollection, centroidCollection, mappableCount } = useProjectsGeoData(projects);

  // Keep callback ref current to avoid stale closure in event listeners
  useEffect(() => {
    onSelectRef.current = onProjectSelect;
  }, [onProjectSelect]);

  // Initialize map on mount
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new MapLibreMap({
      container: containerRef.current,
      style: mapStyleService.getDefaultStyle(),
      center: CENTER,
      zoom: ZOOM,
      pitch: PITCH,
      attributionControl: false,
    });
    map.on('load', () => setMapLoaded(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Sync project layers whenever geodata changes
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    syncProjectLayers(mapRef.current, boundsCollection, centroidCollection);
  }, [mapLoaded, boundsCollection, centroidCollection]);

  // Attach interaction events once when map is ready
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    attachMapEvents(mapRef.current, id => onSelectRef.current(id), hoveredIdRef);
  }, [mapLoaded]);

  // Sync selected feature state
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const prev = prevSelectedIdRef.current;
    if (prev) {
      map.setFeatureState({ source: SRC_BOUNDS, id: prev }, { selected: false });
      map.setFeatureState({ source: SRC_CENTROIDS, id: prev }, { selected: false });
    }
    if (selectedProjectId) {
      map.setFeatureState({ source: SRC_BOUNDS, id: selectedProjectId }, { selected: true });
      map.setFeatureState({ source: SRC_CENTROIDS, id: selectedProjectId }, { selected: true });
    }
    prevSelectedIdRef.current = selectedProjectId;
  }, [mapLoaded, selectedProjectId]);

  return (
    <div className="sa-map-scene">
      <div ref={containerRef} className="sa-map-scene-canvas" />
      <MapOverlay
        count={mappableCount}
        loading={loading}
        hasSelection={selectedProjectId !== null}
      />
    </div>
  );
}
